import { Operation, OperationType } from '../types/History';

interface DocumentHistory {
  undoStack: Operation[];
  redoStack: Operation[];
}

/** Collect content ids from deleteContent operations (undo stack eviction / redo discard). */
export function collectContentIdsFromDeleteOperations(operations: Operation[]): string[] {
  const out = new Set<string>();
  for (const op of operations) {
    if (op.type !== 'deleteContent') continue;
    const arr = Array.isArray(op.data) ? op.data : [op.data];
    for (const item of arr) {
      const id = item?.content?.id as string | undefined;
      if (id) out.add(id);
    }
  }
  return Array.from(out);
}

export class HistoryService {
  private documentHistories: Map<string, DocumentHistory> = new Map();
  private isRecording = true;
  private static instance: HistoryService;
  private currentDocumentId: string | null = null;

  /** Oldest undo steps are dropped when the stack grows past this size. */
  public static readonly MAX_UNDO_STACK = 50;

  private deferredDeleteMediaListener: ((operations: Operation[]) => void) | null = null;

  private constructor() {}

  public static getInstance(): HistoryService {
    if (!HistoryService.instance) {
      HistoryService.instance = new HistoryService();
    }
    return HistoryService.instance;
  }

  /**
   * Invoked when deleteContent operations may need on-disk media cleanup:
   * — bottom of undo stack evicted (cannot reach that state by undo anymore)
   * — redo stack discarded (superseded by a new edit)
   * — document history cleared (document closed / deleted from app)
   */
  setDeferredDeleteMediaListener(listener: ((operations: Operation[]) => void) | null) {
    this.deferredDeleteMediaListener = listener;
  }

  private emitDeferredDeleteMedia(operations: Operation[]) {
    if (operations.length === 0 || !this.deferredDeleteMediaListener) return;
    this.deferredDeleteMediaListener(operations);
  }

  private evictUndoStackBottomIfNeeded() {
    if (!this.currentDocumentId) return;
    const history = this.documentHistories.get(this.currentDocumentId);
    if (!history) return;
    const evicted: Operation[] = [];
    while (history.undoStack.length > HistoryService.MAX_UNDO_STACK) {
      evicted.push(history.undoStack.shift()!);
    }
    this.emitDeferredDeleteMedia(evicted);
  }

  setCurrentDocument(documentId: string | null) {
    this.currentDocumentId = documentId;
    if (documentId && !this.documentHistories.has(documentId)) {
      this.documentHistories.set(documentId, {
        undoStack: [],
        redoStack: [],
      });
    }
  }

  startRecording() {
    this.isRecording = true;
  }

  stopRecording() {
    this.isRecording = false;
  }

  private getOperationName(type: string): string {
    switch (type) {
      case 'splitCategory':
        return 'Split Category';
      case 'groupCategories':
        return 'Group Categories';
      case 'flattenCategory':
        return 'Flatten Category';
      case 'mergeCategories':
        return 'Merge Categories';
      case 'deleteCategory':
        return 'Delete Category';
      case 'addCategory':
        return 'Add Category';
      case 'renameCategory':
        return 'Rename Category';
      case 'deleteContent':
        return 'Delete Content';
      case 'addContent':
        return 'Add Content';
      case 'updateContent':
        return 'Update Content';
      case 'pasteCategory':
        return 'Paste Category';
      case 'dropCategory':
        return 'Drag and Drop Category';
      case 'moveCategoryTo':
        return 'Drag and Drop Category';
      case 'dragCategoryTo':
        return 'Drag Categories Up or Down';
      case 'pasteContent':
        return 'Paste Content';
      case 'moveCategoryUp':
        return 'Move Category Up';
      case 'moveCategoryDown':
        return 'Move Category Down';
      case 'moveContentUp':
        return 'Move Content Up';
      case 'moveContentDown':
        return 'Move Content Down';
      case 'dragContentTo':
        return 'Drag Content Up or Down';
      case 'dropContent':
        return 'Drag and Drop Content';
      case 'renameSubcase':
        return 'Rename Subcase';
      case 'generateSubtree':
        return 'Generate Subtree';
      default:
        return type;
    }
  }

  recordOperation(operation: Operation) {
    if (!this.isRecording || !this.currentDocumentId) return;

    const history = this.documentHistories.get(this.currentDocumentId);
    if (!history) return;

    const discardedRedo = [...history.redoStack];
    history.redoStack = [];
    this.emitDeferredDeleteMedia(discardedRedo);

    // If the operation is part of a batch, add it to the last operation.
    // Do not merge these types: `isBatch` there means cut-vs-copy / multi-item semantics on a *single*
    // user action, not "merge with the next gesture". Otherwise two separate cut-pastes or two moves
    // would become one undo step (same bug as consecutive deletes used to have).
    const noMergeIntoPrevious: OperationType[] = ['deleteContent', 'pasteContent', 'dropContent'];
    if (
      history.undoStack.length > 0 &&
      !noMergeIntoPrevious.includes(operation.type) &&
      history.undoStack[history.undoStack.length - 1].type === operation.type &&
      history.undoStack[history.undoStack.length - 1].isBatch
    ) {
      const lastOp = history.undoStack[history.undoStack.length - 1];
      if (Array.isArray(lastOp.data)) {
        lastOp.data.push(...(Array.isArray(operation.data) ? operation.data : [operation.data]));
      }
    } else {
      history.undoStack.push(operation);
    }
    this.evictUndoStackBottomIfNeeded();
  }

  startBatchOperation(type: OperationType) {
    if (!this.currentDocumentId) return;
    const history = this.documentHistories.get(this.currentDocumentId);
    if (!history) return;

    history.undoStack.push({
      type,
      data: [],
      isBatch: true,
    });
    this.evictUndoStackBottomIfNeeded();
  }

  endBatchOperation() {
    // No action needed, the batch is already recorded
  }

  undo(): Operation | null {
    if (!this.currentDocumentId) return null;
    const history = this.documentHistories.get(this.currentDocumentId);
    if (!history) return null;

    const operation = history.undoStack.pop();
    if (operation) {
      history.redoStack.push(operation);
      return operation;
    }
    return null;
  }

  redo(): Operation | null {
    if (!this.currentDocumentId) return null;
    const history = this.documentHistories.get(this.currentDocumentId);
    if (!history) return null;

    const operation = history.redoStack.pop();
    if (operation) {
      history.undoStack.push(operation);
      return operation;
    }
    return null;
  }

  canUndo(): boolean {
    if (!this.currentDocumentId) return false;
    const history = this.documentHistories.get(this.currentDocumentId);
    return history ? history.undoStack.length > 0 : false;
  }

  canRedo(): boolean {
    if (!this.currentDocumentId) return false;
    const history = this.documentHistories.get(this.currentDocumentId);
    return history ? history.redoStack.length > 0 : false;
  }

  clear() {
    this.documentHistories.clear();
    this.currentDocumentId = null;
  }

  clearDocumentHistory(documentId: string) {
    const history = this.documentHistories.get(documentId);
    if (history) {
      const all = [...history.undoStack, ...history.redoStack];
      this.emitDeferredDeleteMedia(all);
    }
    this.documentHistories.delete(documentId);
    if (this.currentDocumentId === documentId) {
      this.currentDocumentId = null;
    }
  }

  getUndoOperationName(): string {
    if (!this.currentDocumentId) return '';
    const history = this.documentHistories.get(this.currentDocumentId);
    if (!history || history.undoStack.length === 0) return '';
    return this.getOperationName(history.undoStack[history.undoStack.length - 1].type);
  }

  getRedoOperationName(): string {
    if (!this.currentDocumentId) return '';
    const history = this.documentHistories.get(this.currentDocumentId);
    if (!history || history.redoStack.length === 0) return '';
    return this.getOperationName(history.redoStack[history.redoStack.length - 1].type);
  }
}

export const historyService = HistoryService.getInstance();
